import {
  convertV1ToV2,
  type WeeklyReviewContent,
  type WeeklyReviewContentV2,
} from '@onflow/shared';
import { Link } from 'react-router-dom';

import { ProjectionLine } from '../components/ProjectionLine.js';
import { ReceiptsDietBlock } from '../components/ReceiptsDietBlock.js';
import { ReceiptsGroup } from '../components/ReceiptsGroup.js';
import { ReceiptsWeightBlock } from '../components/ReceiptsWeightBlock.js';
import { ReceiptsWorkoutBlock } from '../components/ReceiptsWorkoutBlock.js';
import { ConsistencyLegend, ConsistencyStrip } from '../components/v2/ConsistencyStrip.js';
import { FlowStatus } from '../components/v2/FlowStatus.js';
import { GoalStrip } from '../components/v2/GoalStrip.js';
import { ProjectionChart } from '../components/v2/ProjectionChart.js';
import { Scorecard } from '../components/v2/Scorecard.js';
import { TrackRing, TrackRingLegend } from '../components/v2/TrackRing.js';
import { VerdictBlock } from '../components/VerdictBlock.js';
import { useOnboardingProfile } from '../hooks/useOnboarding.js';
import { useCurrentReviewQuery, useRunReviewMutation } from '../hooks/useReview.js';
import { useWeightLogsQuery } from '../hooks/useWeightLogs.js';
import {
  consistencyDays,
  daysLeft,
  deadlineRange,
  goalHeadline,
  goalProgressPct,
  localDate,
  projectionSeries,
  projectionState,
  reviewScorecardDays,
  reviewScorecardRows,
} from '../lib/track.js';

import '../styles/review-page.css';

/**
 * Review — the v2 weekly-review screen.
 *
 * Implements the Review screen of AppScreen.dc.html ("OnFlow Design System")
 * over the existing /v1/reviews/current contract:
 *
 *   verdict        → VerdictBlock (AI diagnosis, unchanged contract)
 *   goal ring      → weight moved toward target vs share of the window spent
 *   projection     → ProjectionChart over weight logs + the required line,
 *                    with ProjectionLine's deterministic sentence under it
 *   why            → four cause tiles read from this week's adherence
 *   receipts       → Scorecard + 7-day ConsistencyStrip, with the existing
 *                    collapsible diet / workout / weight blocks kept below
 *   goal strip     → the locked goal, closing the loop back to /profile
 *
 * Deviation from the template: no This week / Last week / All time segment —
 * `GET /v1/reviews` (history) does not exist yet (polish item M6.3.1), and a
 * segment whose other tabs cannot load is worse than no segment.
 */
const EYEBROW = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '0.12em',
  textTransform: 'uppercase' as const,
  color: 'var(--ink-3)',
};

const CARD = {
  background: 'var(--paper-1)',
  border: '1px solid var(--edge)',
  borderRadius: 'var(--r-md)',
  boxShadow: 'var(--elev-1)',
};

export function Review() {
  const reviewQuery = useCurrentReviewQuery();
  const runReview = useRunReviewMutation();
  const profile = useOnboardingProfile();
  const weights = useWeightLogsQuery({ pageSize: 30 });

  if (reviewQuery.isLoading) {
    return (
      <section className="review-page fade-in">
        <div className="empty-state card">
          <p>Loading…</p>
        </div>
      </section>
    );
  }
  if (reviewQuery.error) {
    return (
      <section className="review-page fade-in">
        <div className="empty-state card">
          <p role="alert" className="text-red">
            Couldn&apos;t load this week&apos;s review.
          </p>
        </div>
      </section>
    );
  }
  if (!reviewQuery.data) return null;

  const reviewWire = reviewQuery.data.review;
  const reviewId = reviewWire.id;
  const content: WeeklyReviewContent = reviewWire.content;
  const p = profile.data ?? null;
  const modes = p?.trackingModes ?? ['nutrition', 'training'];
  const nutritionEnabled = modes.includes('nutrition');
  const trainingEnabled = modes.includes('training');
  const hasTargets = content.adherence.perDay.some((d) => d.targetKcal > 0);

  if (nutritionEnabled && !hasTargets) {
    return (
      <section className="review-page fade-in">
        <header className="page-header">
          <h1 className="page-title">Your week</h1>
        </header>
        <div className="empty-state card review-page__no-plan">
          <h2>Your nutrition target is missing</h2>
          <p>
            Re-run tracker setup to calculate the deterministic calorie and protein range used by
            your food log and weekly review.
          </p>
          <Link to="/onboarding/mode" className="btn-primary">
            Tracker setup
          </Link>
        </div>
      </section>
    );
  }

  // V1 rows (M6.1) come over the wire untouched so the discriminated union
  // stays intact; normalize to V2 client-side via the shared helper.
  const v2: WeeklyReviewContentV2 =
    content.schemaVersion === 'v2' ? content : convertV1ToV2(content);
  const hasDiagnosis = v2.diagnosis !== '';

  const now = new Date();
  const state = reviewQuery.data.status ?? projectionState(reviewQuery.data.projection);
  const ringState = state === 'neutral' ? 'drifting' : state;
  const accent =
    ringState === 'on-track'
      ? 'var(--on-track)'
      : ringState === 'drifting'
        ? 'var(--drifting)'
        : 'var(--off-track)';

  const logs = weights.data?.items ?? [];
  const latestKg = logs.length > 0 ? logs[0]!.weightKg : null;
  const goalProgress = goalProgressPct(p?.currentWeightKg, p?.targetWeightKg, latestKg) ?? 0;
  const left = daysLeft(p?.targetDate ?? null, now);
  const windowDays = windowLength(p?.createdAt ?? null, p?.targetDate ?? null);
  const elapsedPct =
    windowDays != null && left != null && windowDays > 0
      ? Math.max(0, Math.min(100, ((windowDays - left) / windowDays) * 100))
      : 0;
  const gainedKg =
    p?.currentWeightKg != null && latestKg != null ? latestKg - p.currentWeightKg : null;
  const neededKg =
    p?.currentWeightKg != null && p?.targetWeightKg != null
      ? (p.targetWeightKg - p.currentWeightKg) * (elapsedPct / 100)
      : null;

  const series = projectionSeries(logs, p, now);
  const perDay = v2.adherence.perDay;
  const today = localDate(now);
  const strip = consistencyDays(perDay, today);
  const causes = causeTiles(v2);

  return (
    <section className="review-page fade-in">
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 12,
          padding: '6px 0 14px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <span style={{ ...EYEBROW, display: 'block', marginBottom: 6 }}>
            {formatRange(content.weekStart, content.weekEnd)}
          </span>
          <h1
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-title-size)',
              lineHeight: 'var(--text-title-line)',
              letterSpacing: 'var(--text-title-tracking)',
              fontWeight: 700,
              color: 'var(--ink-1)',
            }}
          >
            Your week
          </h1>
        </div>
        <FlowStatus state={state} />
      </header>

      <VerdictBlock content={v2} createdAt={reviewWire.createdAt} />

      <div
        style={{
          ...CARD,
          border: `1px solid color-mix(in srgb, ${accent} 40%, transparent)`,
          borderRadius: 'var(--r-lg)',
          boxShadow: `var(--elev-1), var(--glow-${ringState})`,
          padding: 22,
          margin: '14px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <TrackRing
          progress={goalProgress}
          elapsed={elapsedPct}
          state={ringState}
          size={218}
          label="Goal progress"
          value={`${Math.round(goalProgress)}%`}
          suffix={left != null ? `${left} days left` : 'No target date'}
        />
        <TrackRingLegend state={ringState} />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1px 1fr',
            gap: 14,
            width: '100%',
            alignItems: 'center',
            paddingTop: 4,
          }}
        >
          <Metric label="Moved" value={gainedKg != null ? signed(gainedKg) : '-'} suffix="kg" />
          <span style={{ background: 'var(--edge)', alignSelf: 'stretch' }} />
          <Metric
            label="Needed by now"
            value={neededKg != null ? signed(neededKg) : '-'}
            suffix="kg"
            tone={accent}
          />
        </div>
      </div>

      <div style={{ ...EYEBROW, margin: '22px 0 10px' }}>Where this pace lands you</div>
      <div style={{ ...CARD, padding: 18 }}>
        {series.points.length > 1 ? (
          <ProjectionChart
            points={series.points}
            required={series.required}
            goalValue={series.goalValue}
            state={ringState}
            height={150}
          />
        ) : null}
        <ProjectionLine projection={reviewQuery.data.projection} />
      </div>

      {!hasDiagnosis ? (
        reviewId === null ? (
          <div className="card review-page__run-diagnostic">
            <h2>Get this week&apos;s AI diagnostic</h2>
            <p className="text-muted">
              See what&apos;s working and what to adjust. Your coach reads this week&apos;s food,
              training, and weight evidence and explains the highest-signal pattern.
            </p>
            <button
              type="button"
              className="btn-primary"
              disabled={runReview.isPending}
              onClick={() => runReview.mutate()}
            >
              {runReview.isPending ? 'Analysing your week…' : 'Run AI diagnostic'}
            </button>
          </div>
        ) : (
          <div className="card review-page__next-review">
            <p className="text-muted">Next review available Sunday 21:00 IST.</p>
          </div>
        )
      ) : null}

      {nutritionEnabled && (
        <>
          <div style={{ ...EYEBROW, margin: '22px 0 10px' }}>Why</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {causes.map((c) => (
              <div key={c.label} style={{ ...CARD, padding: 14 }}>
                <span style={{ ...EYEBROW, fontSize: 11, display: 'block' }}>{c.label}</span>
                <div
                  style={{
                    marginTop: 8,
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-metric-sm-size)',
                    fontWeight: 700,
                    letterSpacing: '-0.03em',
                    fontVariantNumeric: 'tabular-nums',
                    color: c.tone,
                  }}
                >
                  {c.value}
                  <span
                    style={{
                      color: 'var(--ink-3)',
                      fontWeight: 600,
                      fontSize: '0.42em',
                      marginLeft: 5,
                    }}
                  >
                    {c.suffix}
                  </span>
                </div>
                <span
                  style={{
                    display: 'block',
                    marginTop: 7,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    color: 'var(--ink-3)',
                  }}
                >
                  {c.note}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ ...EYEBROW, margin: '22px 0 10px' }}>The receipts</div>
      {nutritionEnabled && (
        <div style={{ ...CARD, padding: 18 }}>
          <Scorecard days={reviewScorecardDays(perDay, today)} rows={reviewScorecardRows(perDay)} />
          <div style={{ height: 1, background: 'var(--edge)', margin: '16px 0 14px' }} />
          <span style={{ ...EYEBROW, fontSize: 11, display: 'block', marginBottom: 10 }}>
            7-day intake
          </span>
          <ConsistencyStrip days={strip} height={52} />
          <div style={{ marginTop: 14 }}>
            <ConsistencyLegend />
          </div>
        </div>
      )}

      <ReceiptsGroup>
        {nutritionEnabled && <ReceiptsDietBlock content={content} />}
        {trainingEnabled && (
          <ReceiptsWorkoutBlock weekStart={content.weekStart} weekEnd={content.weekEnd} />
        )}
        <ReceiptsWeightBlock />
      </ReceiptsGroup>

      <div style={{ marginTop: 18 }}>
        <Link to="/profile" style={{ display: 'block', textDecoration: 'none' }}>
          <GoalStrip
            goal={goalHeadline(p)}
            deadline={deadlineRange(p?.createdAt ?? null, p?.targetDate ?? null)}
            daysLeft={left}
            state={state}
            progress={goalProgress}
          />
        </Link>
      </div>
    </section>
  );
}

function Metric({
  label,
  value,
  suffix,
  tone,
}: {
  label: string;
  value: string;
  suffix: string;
  tone?: string;
}) {
  return (
    <div style={{ textAlign: 'center' }}>
      <span style={{ ...EYEBROW, fontSize: 11, display: 'block' }}>{label}</span>
      <div
        style={{
          marginTop: 8,
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-metric-sm-size)',
          fontWeight: 700,
          letterSpacing: '-0.03em',
          fontVariantNumeric: 'tabular-nums',
          color: tone,
        }}
      >
        {value}
        <span style={{ color: 'var(--ink-3)', fontWeight: 600, fontSize: '0.42em', marginLeft: 5 }}>
          {suffix}
        </span>
      </div>
    </div>
  );
}

interface CauseTile {
  label: string;
  value: string;
  suffix: string;
  note: string;
  tone: string;
}

/**
 * The "why" tiles. Every number comes from this week's adherence rows — no
 * AI text, so the tiles stay honest even before a diagnosis has run.
 */
function causeTiles(v2: WeeklyReviewContentV2): CauseTile[] {
  const perDay = v2.adherence.perDay;
  const logged = perDay.filter((d) => d.actualKcal > 0);
  const over = logged.filter((d) => d.actualKcal > d.targetKcal && !d.dayMet).length;
  const proteinHit = perDay.filter((d) => d.proteinWithinTolerance).length;
  const avgDelta =
    logged.length > 0
      ? Math.round(logged.reduce((s, d) => s + (d.actualKcal - d.targetKcal), 0) / logged.length)
      : 0;
  const weight = v2.weightTrend;

  return [
    {
      label: 'Avg delta',
      value: `${avgDelta > 0 ? '+' : ''}${avgDelta}`,
      suffix: 'kcal/day',
      note: logged.length > 0 ? `over ${logged.length} logged days` : 'nothing logged yet',
      tone: Math.abs(avgDelta) < 150 ? 'var(--on-track)' : 'var(--off-track)',
    },
    {
      label: 'Days over',
      value: String(over),
      suffix: `of ${perDay.length}`,
      note: over === 0 ? 'no blowouts' : 'where the gap comes from',
      tone: over === 0 ? 'var(--on-track)' : 'var(--off-track)',
    },
    {
      label: 'Protein hit',
      value: String(proteinHit),
      suffix: `of ${perDay.length}`,
      note: proteinHit >= 5 ? 'good enough' : 'the easiest fix',
      tone: proteinHit >= 5 ? 'var(--on-track)' : 'var(--drifting)',
    },
    {
      label: 'Weight',
      value: weight ? `${weight.deltaKg > 0 ? '+' : ''}${weight.deltaKg.toFixed(1)}` : '-',
      suffix: 'kg',
      note: weight ? `${weight.dataPoints.length} weigh-ins` : 'no weigh-ins this week',
      tone: 'var(--ink-1)',
    },
  ];
}

function signed(n: number): string {
  return `${n > 0 ? '+' : ''}${n.toFixed(1)}`;
}

/** Whole days between the profile's start and its target date. */
function windowLength(startIso: string | null, targetIso: string | null): number | null {
  if (!startIso || !targetIso) return null;
  const days = Math.round(
    (new Date(targetIso).getTime() - new Date(startIso).getTime()) / 86_400_000,
  );
  return days > 0 ? days : null;
}

function formatRange(weekStartIso: string, weekEndIso: string): string {
  const start = new Date(weekStartIso);
  const end = new Date(weekEndIso);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const sameYear = start.getFullYear() === end.getFullYear();
  const startStr = start.toLocaleDateString(undefined, opts);
  const endStr = end.toLocaleDateString(undefined, opts);
  return sameYear
    ? `${startStr} – ${endStr}, ${end.getFullYear()}`
    : `${startStr}, ${start.getFullYear()} – ${endStr}, ${end.getFullYear()}`;
}
